import React from 'react';
import { Link } from 'react-router-dom';
import { Button } from '../components/ui/button';
import { 
  Home, CheckCircle, Users, Shield, Calendar, ClipboardCheck, 
  FileText, ThumbsUp, Award, Phone, ChevronRight, Clock, AlertCircle, ClipboardList, Map, Quote
} from 'lucide-react';
import HomeProfilePreview from '../components/HomeProfilePreview';

const AssessmentPage = () => {
  return (
    <div className="min-h-screen">
      {/* Waitlist Banner */}
      <div className="bg-[#CFB973] py-4 mt-[120px]">
        <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex flex-col md:flex-row items-center justify-center gap-3 text-center">
            <Clock className="w-6 h-6 text-[#003366]" />
            <p className="text-[#003366] font-semibold">
              <span className="block sm:inline">Currently at capacity.</span>{' '}
              <span className="block sm:inline">Join our waitlist — scheduling opens soon.</span>
            </p>
          </div>
        </div>
      </div>

      {/* Hero Section - Premium Style */}
      <section className="relative py-20 md:py-24 bg-[#003366] text-white overflow-hidden">
        {/* Blueprint pattern */}
        <div className="absolute inset-0 opacity-[0.03]" style={{
          backgroundImage: `url("data:image/svg+xml,%3Csvg width='100' height='100' viewBox='0 0 100 100' xmlns='http://www.w3.org/2000/svg'%3E%3Cpath d='M0 0h100v100H0z' fill='none' stroke='%23ffffff' stroke-width='0.5'/%3E%3Cpath d='M20 0v100M40 0v100M60 0v100M80 0v100M0 20h100M0 40h100M0 60h100M0 80h100' stroke='%23ffffff' stroke-width='0.25'/%3E%3C/svg%3E")`,
          backgroundSize: '100px 100px'
        }}></div>
        {/* Logo Watermark */}
        <div className="absolute inset-0 flex items-center justify-center pointer-events-none">
          <img src="https://customer-assets.emergentagent.com/job_waitlist-system-5/artifacts/0d36dd51_Transparent%20Png-02.png" alt="" className="w-[400px] md:w-[500px] h-auto opacity-[0.06]" />
        </div>
        
        <div className="relative z-10 max-w-5xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <h1 className="mb-6">
            <span className="block text-4xl sm:text-5xl md:text-6xl lg:text-7xl font-bold text-white leading-tight tracking-tight mb-2">
              Whole-Home
            </span>
            <span className="block text-4xl sm:text-5xl md:text-6xl lg:text-7xl font-bold leading-tight tracking-tight text-[#CFB973]">
              Assessment
            </span>
          </h1>
          
          {/* Decorative line */}
          <div className="flex items-center justify-center gap-4 mb-6">
            <div className="w-12 h-0.5 bg-[#CFB973]"></div>
            <div className="w-2 h-2 bg-[#CFB973] rotate-45"></div>
            <div className="w-12 h-0.5 bg-[#CFB973]"></div>
          </div>
          
          <p className="text-base md:text-lg text-white/80 mb-2 max-w-3xl mx-auto">
            One visit. A complete picture of your home.
          </p>
          <p className="text-xl md:text-2xl font-bold text-[#CFB973] mb-8 max-w-3xl mx-auto">
            The foundation for everything that follows.
          </p>
          
          {/* Price Badge */}
          <div className="inline-flex items-center gap-3 bg-[#CFB973] text-[#003366] font-bold text-2xl md:text-3xl px-8 py-4 rounded-xl mb-4 shadow-xl">
            <span>Starting at $249</span>
          </div>
          <p className="text-white/70 text-sm mb-8">
            <span className="block sm:inline">One-time fee • No subscription</span>
            <span className="hidden sm:inline"> • </span>
            <span className="block sm:inline">100% Satisfaction Guarantee</span>
          </p>
          
          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <Link to="/contact">
              <Button size="lg" className="bg-[#CFB973] hover:bg-[#CFB973]/90 text-[#003366] font-bold px-10 py-7 text-lg shadow-xl">
                Join the Waitlist
              </Button>
            </Link>
            <a href="tel:5513799774">
              <Button size="lg" className="bg-transparent hover:bg-white/10 text-white font-semibold px-10 py-7 text-lg border-2 border-[#CFB973]">
                <Phone className="mr-2" size={20} />
                (551) 379-9774
              </Button>
            </a>
          </div>
        </div>
      </section>

      {/* Trust Badges - Gold Circles */}
      <section className="py-8 bg-[#E8EEF3]">
        <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex flex-wrap justify-center items-center gap-8 md:gap-16">
            <div className="flex flex-col items-center">
              <div className="w-20 h-20 rounded-full bg-[#CFB973] flex items-center justify-center mb-3 shadow-lg">
                <Shield className="w-10 h-10 text-[#003366]" />
              </div>
              <p className="text-[#003366] font-bold text-sm text-center">Licensed & Insured</p>
              <p className="text-[#003366]/60 text-xs">#13VH13590600</p>
            </div>
            
            <div className="flex flex-col items-center">
              <div className="w-20 h-20 rounded-full bg-[#CFB973] flex items-center justify-center mb-3 shadow-lg">
                <ThumbsUp className="w-10 h-10 text-[#003366]" />
              </div>
              <p className="text-[#003366] font-bold text-sm text-center">100% Satisfaction</p>
              <p className="text-[#003366]/60 text-xs">Guarantee</p>
            </div>
            
            <div className="flex flex-col items-center">
              <div className="w-20 h-20 rounded-full bg-[#CFB973] flex items-center justify-center mb-3 shadow-lg">
                <Award className="w-10 h-10 text-[#003366]" />
              </div>
              <p className="text-[#003366] font-bold text-sm text-center">10+ Years</p>
              <p className="text-[#003366]/60 text-xs">Experience</p>
            </div>
          </div>
        </div>
      </section>

      {/* 3-Step Path Section */}
      <section className="py-20 bg-white">
        <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-12">
            <h2 className="text-3xl md:text-4xl font-bold text-[#003366] mb-4">
              Our 3-Step Path to a Worry-Free Home
            </h2>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-8 mb-16">
            {/* Step 1 */}
            <div className="text-center">
              <div className="w-20 h-20 rounded-full bg-[#CFB973] flex items-center justify-center mx-auto mb-6 shadow-lg">
                <ClipboardList className="w-10 h-10 text-[#003366]" />
              </div>
              <div className="bg-[#003366] text-white text-sm font-bold px-4 py-1 rounded-full inline-block mb-4">Step 1</div>
              <h3 className="text-xl font-bold text-[#003366] mb-4">The Deep-Dive Assessment</h3>
              <p className="text-gray-600 leading-relaxed">
                We perform a comprehensive, top-to-bottom evaluation of your home's critical systems — from attic to foundation. We identify hidden vulnerabilities, safety concerns, and efficiency gaps that standard inspections miss.
              </p>
            </div>

            {/* Step 2 */}
            <div className="text-center">
              <div className="w-20 h-20 rounded-full bg-[#CFB973] flex items-center justify-center mx-auto mb-6 shadow-lg">
                <Map className="w-10 h-10 text-[#003366]" />
              </div>
              <div className="bg-[#003366] text-white text-sm font-bold px-4 py-1 rounded-full inline-block mb-4">Step 2</div>
              <h3 className="text-xl font-bold text-[#003366] mb-4">Your Custom Care Plan</h3>
              <p className="text-gray-600 leading-relaxed">
                Data is nothing without a plan. You'll receive a prioritized Valenrock™ Care Roadmap — what needs immediate attention, what to monitor, and a seasonal schedule to keep your home running perfectly.
              </p>
            </div>

            {/* Step 3 */}
            <div className="text-center">
              <div className="w-20 h-20 rounded-full bg-[#CFB973] flex items-center justify-center mx-auto mb-6 shadow-lg">
                <Shield className="w-10 h-10 text-[#003366]" />
              </div>
              <div className="bg-[#003366] text-white text-sm font-bold px-4 py-1 rounded-full inline-block mb-4">Step 3</div>
              <h3 className="text-xl font-bold text-[#003366] mb-4">Ongoing Peace of Mind</h3>
              <p className="text-gray-600 leading-relaxed">
                Handle the list yourself or let our professionals manage it for you. With your Care Plan in place, you'll prevent emergency repair costs and increase your home's long-term value.
              </p>
            </div>
          </div>

          {/* Social Proof Quote */}
          <div className="bg-[#E8EEF3] rounded-2xl p-8 md:p-10 mb-12">
            <div className="text-center">
              <Quote className="w-10 h-10 text-[#CFB973] mb-4 mx-auto" />
              <p className="text-[#003366] text-lg italic mb-4 max-w-3xl mx-auto">
                "The assessment Valenrock™ did for our home in Bergen County saved us thousands in potential water damage. They caught issues we never would have found on our own."
              </p>
              <p className="text-gray-600 font-semibold">— Satisfied Homeowner, Bergen County</p>
            </div>
          </div>

          {/* CTA */}
          <div className="text-center">
            <Link to="/contact">
              <Button size="lg" className="bg-[#CFB973] hover:bg-[#CFB973]/90 text-[#003366] font-bold px-12 py-7 text-xl shadow-xl">
                Schedule My Assessment
              </Button>
            </Link>
          </div>
        </div>
      </section>

      {/* What Is It Section */}
      <section className="py-20 bg-white">
        <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-start">
            <div>
              <h2 className="text-3xl md:text-4xl font-bold text-[#003366] mb-6">What Is a Whole-Home Assessment?</h2>
              <p className="text-gray-700 text-lg mb-6">
                A thorough walkthrough of your home by a licensed professional. We document condition, identify issues, and give you a clear plan — <strong>not a sales pitch</strong>.
              </p>

              {/* Assessor Image */}
              <div className="mb-8 rounded-xl overflow-hidden shadow-lg">
                <img 
                  src="https://customer-assets.emergentagent.com/job_waitlist-system-5/artifacts/r3rkoiu2_IMG_3441.jpeg" 
                  alt="Valenrock Home Services Assessor" 
                  className="w-full object-cover"
                />
              </div>
              
              <div className="bg-[#E8EEF3] rounded-xl p-6 mb-8">
                <p className="text-[#003366] font-semibold mb-2">Not a home inspection.</p>
                <p className="text-gray-600 text-sm">
                  Inspections are pass/fail for buyers. Our assessment is ongoing guidance for homeowners who want to plan ahead and maintain their home's value.
                </p>
              </div>
              
              <h3 className="font-bold text-[#003366] text-xl mb-4">What You Get:</h3>
              <ul className="space-y-4">
                {[
                  'Full walkthrough of all major systems',
                  'Priority issues ranked by urgency',
                  'Recommended timeline for repairs',
                  'Appliance inventory with brand & model info',
                  'Your personalized Home Profile document',
                  '100% satisfaction guarantee'
                ].map((item, idx) => (
                  <li key={idx} className="flex items-start gap-3">
                    <div className="w-7 h-7 rounded-full bg-[#CFB973] flex items-center justify-center flex-shrink-0 mt-0.5">
                      <CheckCircle className="w-4 h-4 text-[#003366]" />
                    </div>
                    <span className="text-gray-700">{item}</span>
                  </li>
                ))}
              </ul>
            </div>

            {/* Pricing Card - Premium Style */}
            <div className="bg-white p-8 rounded-2xl shadow-2xl border-4 border-[#CFB973] sticky top-24">
              <div className="text-center mb-8">
                <h3 className="text-2xl font-bold text-[#003366] mb-2">Transparent Pricing.</h3>
                <p className="text-[#CFB973] font-semibold">No Hidden Fees.</p>
              </div>
              
              {/* Pricing Tiers */}
              <div className="space-y-4 mb-6">
                {/* Starter */}
                <div className="bg-gradient-to-r from-[#E8EEF3] to-white p-4 rounded-xl border border-[#003366]/10">
                  <div className="flex justify-between items-start mb-2">
                    <div>
                      <span className="bg-[#003366] text-white text-xs font-bold px-2 py-1 rounded">STARTER</span>
                    </div>
                    <span className="text-2xl font-bold text-[#003366]">$249</span>
                  </div>
                  <p className="text-gray-600 text-sm">Perfect for condos & cottages</p>
                  <p className="text-[#003366]/60 text-xs mt-1">Up to 1,500 sq ft</p>
                </div>
                
                {/* Standard - Most Popular */}
                <div className="bg-gradient-to-r from-[#CFB973]/20 to-[#CFB973]/5 p-4 rounded-xl border-2 border-[#CFB973] relative">
                  <div className="absolute -top-3 left-1/2 transform -translate-x-1/2">
                    <span className="bg-[#CFB973] text-[#003366] text-xs font-bold px-3 py-1 rounded-full">MOST POPULAR</span>
                  </div>
                  <div className="flex justify-between items-start mb-2 mt-2">
                    <div>
                      <span className="bg-[#003366] text-white text-xs font-bold px-2 py-1 rounded">STANDARD</span>
                    </div>
                    <span className="text-2xl font-bold text-[#003366]">$449</span>
                  </div>
                  <p className="text-gray-700 text-sm font-medium">Our most popular for family homes</p>
                  <p className="text-[#003366]/60 text-xs mt-1">Up to 3,000 sq ft</p>
                </div>
                
                {/* Estate */}
                <div className="bg-gradient-to-r from-[#E8EEF3] to-white p-4 rounded-xl border border-[#003366]/10">
                  <div className="flex justify-between items-start mb-2">
                    <div>
                      <span className="bg-[#003366] text-white text-xs font-bold px-2 py-1 rounded">ESTATE</span>
                    </div>
                    <span className="text-2xl font-bold text-[#003366]">$649+</span>
                  </div>
                  <p className="text-gray-600 text-sm">Deep-dive for large or historic properties</p>
                  <p className="text-[#003366]/60 text-xs mt-1">4,000+ sq ft</p>
                </div>
              </div>
              
              {/* All Plans Include */}
              <div className="bg-[#003366]/5 rounded-lg p-3 mb-6 text-center">
                <p className="text-sm text-[#003366]">
                  <span className="text-[#CFB973] mr-1">✦</span>
                  All plans include a <span className="font-bold">Digital Home Profile™</span> in 48 hours
                  <span className="text-[#CFB973] ml-1">✦</span>
                </p>
              </div>
              
              <Link to="/contact">
                <Button className="w-full bg-[#003366] hover:bg-[#003366]/90 text-white py-6 text-lg font-bold shadow-lg">
                  Book Your Assessment
                </Button>
              </Link>
              <p className="text-gray-400 text-xs text-center mt-4">Final pricing confirmed after a brief phone consultation.</p>
              
              {/* Quick Contact */}
              <div className="mt-6 pt-6 border-t border-gray-100 text-center">
                <p className="text-gray-500 text-sm mb-2">Questions? Call us:</p>
                <a href="tel:5513799774" className="text-[#003366] font-bold text-lg hover:text-[#CFB973] transition-colors">
                  (551) 379-9774
                </a>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* How It Works - 4 Steps with Gold Icons */}
      <section className="py-20 bg-[#E8EEF3]">
        <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <h2 className="text-3xl md:text-4xl font-bold text-[#003366] mb-4">How It Works</h2>
            <p className="text-gray-600 max-w-2xl mx-auto">
              From booking to receiving your Home Profile — it's simple and stress-free.
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-4 gap-8">
            {/* Step 1 */}
            <div className="text-center">
              <div className="relative mb-6">
                <div className="w-24 h-24 rounded-full bg-[#CFB973] flex items-center justify-center mx-auto shadow-lg">
                  <Phone className="w-12 h-12 text-[#003366]" />
                </div>
                <div className="absolute -top-2 -right-2 w-8 h-8 rounded-full bg-[#003366] text-white flex items-center justify-center font-bold text-sm md:right-1/4 md:translate-x-1/2">
                  1
                </div>
              </div>
              <h3 className="text-xl font-bold text-[#003366] mb-2">Book</h3>
              <p className="text-gray-600 text-sm">Schedule a time that works for you — online or by phone.</p>
            </div>

            {/* Step 2 */}
            <div className="text-center">
              <div className="relative mb-6">
                <div className="w-24 h-24 rounded-full bg-[#CFB973] flex items-center justify-center mx-auto shadow-lg">
                  <Home className="w-12 h-12 text-[#003366]" />
                </div>
                <div className="absolute -top-2 -right-2 w-8 h-8 rounded-full bg-[#003366] text-white flex items-center justify-center font-bold text-sm md:right-1/4 md:translate-x-1/2">
                  2
                </div>
              </div>
              <h3 className="text-xl font-bold text-[#003366] mb-2">Walkthrough</h3>
              <p className="text-gray-600 text-sm">We tour your home together, reviewing all major systems.</p>
            </div>

            {/* Step 3 */}
            <div className="text-center">
              <div className="relative mb-6">
                <div className="w-24 h-24 rounded-full bg-[#CFB973] flex items-center justify-center mx-auto shadow-lg">
                  <ClipboardCheck className="w-12 h-12 text-[#003366]" />
                </div>
                <div className="absolute -top-2 -right-2 w-8 h-8 rounded-full bg-[#003366] text-white flex items-center justify-center font-bold text-sm md:right-1/4 md:translate-x-1/2">
                  3
                </div>
              </div>
              <h3 className="text-xl font-bold text-[#003366] mb-2">Profile Delivered</h3>
              <p className="text-gray-600 text-sm">Receive your detailed Home Profile within 48 hours.</p>
            </div>

            {/* Step 4 */}
            <div className="text-center">
              <div className="relative mb-6">
                <div className="w-24 h-24 rounded-full bg-[#CFB973] flex items-center justify-center mx-auto shadow-lg">
                  <CheckCircle className="w-12 h-12 text-[#003366]" />
                </div>
                <div className="absolute -top-2 -right-2 w-8 h-8 rounded-full bg-[#003366] text-white flex items-center justify-center font-bold text-sm md:right-1/4 md:translate-x-1/2">
                  4
                </div>
              </div>
              <h3 className="text-xl font-bold text-[#003366] mb-2">Your Choice</h3>
              <p className="text-gray-600 text-sm">Plan maintenance, repairs, or renovations on your timeline.</p>
            </div>
          </div>

          {/* Tagline */}
          <div className="text-center mt-12 py-6 border-t-2 border-b-2 border-[#CFB973]/30">
            <p className="text-xl md:text-2xl text-[#003366] font-medium italic">
              "No subscription. No pressure. Just clarity and a plan built around your goals."
            </p>
          </div>
        </div>
      </section>

      {/* Home Profile Preview Component */}
      <HomeProfilePreview />

      {/* Comparison Table - Navy Background */}
      <section className="py-20 bg-[#003366] relative overflow-hidden">
        {/* Blueprint pattern */}
        <div className="absolute inset-0 opacity-[0.03]" style={{
          backgroundImage: `url("data:image/svg+xml,%3Csvg width='100' height='100' viewBox='0 0 100 100' xmlns='http://www.w3.org/2000/svg'%3E%3Cpath d='M0 0h100v100H0z' fill='none' stroke='%23ffffff' stroke-width='0.5'/%3E%3Cpath d='M20 0v100M40 0v100M60 0v100M80 0v100M0 20h100M0 40h100M0 60h100M0 80h100' stroke='%23ffffff' stroke-width='0.25'/%3E%3C/svg%3E")`,
          backgroundSize: '100px 100px'
        }}></div>
        
        <div className="relative z-10 max-w-5xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-12">
            <h2 className="text-3xl md:text-4xl font-bold text-white mb-4">Why This Beats the Alternatives</h2>
            <p className="text-white/70">Most options leave you guessing. We give you the full picture.</p>
          </div>

          <div className="overflow-x-auto">
            <table className="w-full bg-white rounded-2xl shadow-2xl overflow-hidden text-sm border-4 border-[#CFB973]">
              <thead>
                <tr className="bg-[#003366]">
                  <th className="px-6 py-4 text-left font-bold text-white">Feature</th>
                  <th className="px-6 py-4 text-center font-bold bg-[#CFB973] text-[#003366]">Valenrock™</th>
                  <th className="px-6 py-4 text-center font-bold text-white">Home Warranty</th>
                  <th className="px-6 py-4 text-center font-bold text-white">On-Demand Apps</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-100">
                <tr>
                  <td className="px-6 py-4 text-gray-700 font-medium">Knows your home</td>
                  <td className="px-6 py-4 text-center bg-[#CFB973]/10">
                    <CheckCircle className="text-green-500 mx-auto" size={22} />
                  </td>
                  <td className="px-6 py-4 text-center text-gray-400">—</td>
                  <td className="px-6 py-4 text-center text-gray-400">—</td>
                </tr>
                <tr className="bg-gray-50">
                  <td className="px-6 py-4 text-gray-700 font-medium">Proactive recommendations</td>
                  <td className="px-6 py-4 text-center bg-[#CFB973]/10">
                    <CheckCircle className="text-green-500 mx-auto" size={22} />
                  </td>
                  <td className="px-6 py-4 text-center text-gray-400">—</td>
                  <td className="px-6 py-4 text-center text-gray-400">—</td>
                </tr>
                <tr>
                  <td className="px-6 py-4 text-gray-700 font-medium">No annual contract</td>
                  <td className="px-6 py-4 text-center bg-[#CFB973]/10">
                    <CheckCircle className="text-green-500 mx-auto" size={22} />
                  </td>
                  <td className="px-6 py-4 text-center text-red-400 font-bold">✗</td>
                  <td className="px-6 py-4 text-center">
                    <CheckCircle className="text-green-500 mx-auto" size={22} />
                  </td>
                </tr>
                <tr className="bg-gray-50">
                  <td className="px-6 py-4 text-gray-700 font-medium">Same team every time</td>
                  <td className="px-6 py-4 text-center bg-[#CFB973]/10">
                    <CheckCircle className="text-green-500 mx-auto" size={22} />
                  </td>
                  <td className="px-6 py-4 text-center text-red-400 font-bold">✗</td>
                  <td className="px-6 py-4 text-center text-red-400 font-bold">✗</td>
                </tr>
                <tr>
                  <td className="px-6 py-4 text-gray-700 font-bold">Typical Cost</td>
                  <td className="px-6 py-4 text-center bg-[#CFB973]/10 font-bold text-[#003366]">One-time from $249</td>
                  <td className="px-6 py-4 text-center text-gray-600">$400-600/yr + fees</td>
                  <td className="px-6 py-4 text-center text-gray-600">$75-300/visit</td>
                </tr>
              </tbody>
            </table>
          </div>
        </div>
      </section>

      {/* FAQ Section */}
      <section className="py-20 bg-white">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-12">
            <h2 className="text-3xl md:text-4xl font-bold text-[#003366] mb-4">Common Questions</h2>
          </div>

          <div className="space-y-4">
            {[
              {
                q: "I already have a home inspection report. Why do I need this?",
                a: "Your inspection told you what's wrong. We tell you what matters, what it costs, what to do first, and who to trust. An inspection is a snapshot — your Home Profile is an operating system for your home."
              },
              {
                q: "How is this different from a home inspection?",
                a: "Inspections are pass/fail for buyers — written for liability, not clarity. Our assessment is ongoing guidance for homeowners who want to plan ahead, prioritize smartly, and avoid expensive surprises."
              },
              {
                q: "How long does the assessment take?",
                a: "Typically 1-2 hours depending on the size of your home. We're thorough but respectful of your time."
              },
              {
                q: "Do I need to be home during the assessment?",
                a: "Yes, we prefer you're present so we can discuss findings in real-time and understand your priorities."
              },
              {
                q: "What areas do you serve?",
                a: "We serve Northern & Central New Jersey, including Essex, Bergen, Morris, Passaic, Hudson, Middlesex, Somerset, and Union counties."
              },
              {
                q: "Why wouldn't I just call contractors directly?",
                a: "Because contractors optimize for their trade. We optimize for your home. You get one trusted partner who sees the full picture — not five vendors who each see their piece."
              },
              {
                q: "Do you do all the work yourselves?",
                a: "We coordinate a vetted network of licensed, insured specialists — plumbers, electricians, HVAC techs, roofers, and more. You deal with us; we handle the coordination. Accountability stays with Valenrock™."
              }
            ].map((faq, idx) => (
              <div key={idx} className="bg-[#E8EEF3] rounded-xl p-6">
                <h3 className="font-bold text-[#003366] mb-2 flex items-center gap-2">
                  <ChevronRight className="text-[#CFB973]" size={20} />
                  {faq.q}
                </h3>
                <p className="text-gray-600 ml-7">{faq.a}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Final CTA */}
      <section className="py-20 bg-[#003366] relative overflow-hidden">
        {/* Blueprint pattern */}
        <div className="absolute inset-0 opacity-[0.03]" style={{
          backgroundImage: `url("data:image/svg+xml,%3Csvg width='100' height='100' viewBox='0 0 100 100' xmlns='http://www.w3.org/2000/svg'%3E%3Cpath d='M0 0h100v100H0z' fill='none' stroke='%23ffffff' stroke-width='0.5'/%3E%3Cpath d='M20 0v100M40 0v100M60 0v100M80 0v100M0 20h100M0 40h100M0 60h100M0 80h100' stroke='%23ffffff' stroke-width='0.25'/%3E%3C/svg%3E")`,
          backgroundSize: '100px 100px'
        }}></div>
        
        <div className="relative z-10 max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <h2 className="text-3xl md:text-4xl font-bold text-white mb-4">Secure Your Spot</h2>
          <p className="text-white/80 mb-8 text-lg">
            Join the waitlist now. We'll contact you when scheduling opens.
          </p>
          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <Link to="/contact">
              <Button size="lg" className="bg-[#CFB973] hover:bg-[#CFB973]/90 text-[#003366] font-bold px-12 py-7 text-xl shadow-xl">
                Join the Waitlist
              </Button>
            </Link>
          </div>
          <p className="text-white/60 text-sm mt-6">Starting at $249 • Northern & Central NJ</p>
          
          {/* Contact Info */}
          <div className="mt-10 pt-8 border-t border-white/20 flex flex-col sm:flex-row justify-center items-center gap-6 text-white/70">
            <a href="tel:5513799774" className="flex items-center gap-2 hover:text-[#CFB973] transition-colors">
              <Phone size={18} />
              <span>(551) 379-9774</span>
            </a>
            <span className="hidden sm:block">•</span>
            <span>contact@valenrockhome.services</span>
          </div>
        </div>
      </section>
    </div>
  );
};

export default AssessmentPage;
